import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import StandaloneSystemHeader from "../components/branding/StandaloneSystemHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import ThemeToggle from "../components/theme/ThemeToggle";

function mapPreviewError(status, err) {
  if (!status) return "Der Server konnte nicht erreicht werden.";
  if (status === 400) return extractApiError(err, "Der Einladungslink ist ungültig oder unvollständig.");
  if (status === 404) return "Diese Einladung wurde nicht gefunden oder ist ungültig.";
  if (status === 410) return "Diese Einladung ist abgelaufen. Bitte fordere eine neue Einladung an.";
  if (status === 409) return "Diese Einladung wurde bereits verwendet oder der Benutzer existiert bereits.";
  if (status === 422) return "Bitte prüfe deine Eingaben.";
  return "Es ist ein technischer Fehler aufgetreten. Bitte versuche es später erneut.";
}

function mapAcceptError(status, err) {
  if (!status) return "Der Server konnte nicht erreicht werden.";
  if (status === 400) return extractApiError(err, "Die eingegebenen Daten konnten nicht verarbeitet werden.");
  if (status === 404) return "Diese Einladung wurde nicht gefunden oder ist ungültig.";
  if (status === 410) return "Diese Einladung ist abgelaufen. Bitte fordere eine neue Einladung an.";
  if (status === 409) return "Diese Einladung wurde bereits verwendet oder der Benutzer existiert bereits.";
  if (status === 422) return "Bitte prüfe deine Eingaben.";
  return "Es ist ein technischer Fehler aufgetreten. Bitte versuche es später erneut.";
}

function roleLabel(role) {
  if (role === "TENANT_ADMIN") return "Mandant-Admin";
  if (role === "SALES_USER") return "Sales";
  if (role === "VIEWER") return "Viewer";
  return role || "—";
}

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let alive = true;
    setError("");
    setPreview(null);
    setAccepted(false);

    if (!token) {
      setError("Der Einladungslink ist ungültig oder unvollständig.");
      return () => {
        alive = false;
      };
    }

    async function run() {
      setLoading(true);
      try {
        const r = await api.get("/api/public/invites/preview", { params: { token } });
        if (!alive) return;
        setPreview(r.data);
      } catch (err) {
        if (!alive) return;
        const status = err?.response?.status;
        setError(mapPreviewError(status, err));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const p1 = password;
    const p2 = passwordConfirm;
    if (!p1 || !p2) {
      setSubmitError("Bitte gib ein Passwort ein und bestätige es.");
      return;
    }
    if (p1 !== p2) {
      setSubmitError("Die Passwörter stimmen nicht überein.");
      return;
    }
    if (p1.length < 8) {
      setSubmitError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (!token) {
      setSubmitError("Der Einladungslink ist ungültig oder unvollständig.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/api/public/invites/accept", {
        token,
        password: p1,
        password_confirm: p2,
      });
      setAccepted(true);
    } catch (err) {
      const status = err?.response?.status;
      setSubmitError(mapAcceptError(status, err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-radial-brand" aria-hidden />

      <div className="absolute top-4 right-4 z-30">
        <ThemeToggle />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <StandaloneSystemHeader />

          <Card className="backdrop-blur-xl">
            <CardContent className="p-6 sm:p-8">
              <h1 className="font-display text-2xl font-semibold tracking-tight">
                Einladung annehmen
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Du wurdest eingeladen, einem FyreCRM-Mandanten beizutreten.
              </p>

              {loading && (
                <div className="mt-6 text-sm text-muted-foreground">
                  Einladung wird geladen …
                </div>
              )}

              {!loading && error && (
                <div className="mt-6 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2.5">
                  {error}
                </div>
              )}

              {!loading && !error && preview && !accepted && (
                <>
                  <div className="mt-6 space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Mandant</span>
                      <span className="font-medium">{preview.tenant_name || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">E-Mail</span>
                      <span className="font-medium">{preview.invited_email || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium">{preview.full_name || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Rolle</span>
                      <span className="font-medium">{roleLabel(preview.role)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Gültig bis</span>
                      <span className="font-medium">{formatDateTime(preview.expires_at)}</span>
                    </div>
                  </div>

                  <form className="space-y-4 mt-6" onSubmit={handleSubmit}>
                    <div className="space-y-1.5">
                      <Label htmlFor="password">Passwort</Label>
                      <Input
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="passwordConfirm">Passwort bestätigen</Label>
                      <Input
                        id="passwordConfirm"
                        type="password"
                        autoComplete="new-password"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        required
                      />
                    </div>

                    {submitError && (
                      <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2.5">
                        {submitError}
                      </div>
                    )}

                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? "Wird verarbeitet …" : "Passwort setzen und Einladung annehmen"}
                    </Button>
                  </form>
                </>
              )}

              {!loading && !error && accepted && (
                <div className="mt-6 space-y-4">
                  <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2.5">
                    Dein Zugang wurde erstellt. Du kannst dich jetzt einloggen.
                  </div>
                  <Button asChild className="w-full">
                    <Link to="/login">Zur Anmeldung</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

